/**
 * Shared UI-action breadcrumb observer.
 *
 * Installs delegated capture-phase listeners on `document` for click, submit,
 * and change (select / checkbox / radio only) events. Each qualifying event is
 * logged as an `app_action` breadcrumb via the provided logger's
 * `info` method, so downstream consumers can route these breadcrumbs
 * uniformly.
 *
 * **Idempotent**: multiple installs on the same document (e.g. Admin and
 * Content Studio running in the same page) share one listener set and one
 * coalescing state — a single click emits a single breadcrumb, not one per
 * install. The logger from the first install is used for all events;
 * subsequent installs' loggers are ignored to avoid duplicate breadcrumbs.
 * Cleanup is reference-counted: each install returns its own cleanup function,
 * but listeners are removed only after the final cleanup. Each cleanup is
 * idempotent (safe to call more than once).
 *
 * Design constraints:
 * - **Non-blocking**: the handler body is synchronous and cheap; logger calls
 *   are wrapped in try/catch so a logger failure can never throw into the event
 *   loop.
 * - **Bounded**: target metadata and route are each truncated to 80 characters.
 * - **Safe**: no `preventDefault`, `stopPropagation`, or DOM/event mutation.
 * - **No keystrokes**: keyboard events are never observed.
 * - **Coalesced**: identical action+target signatures within a 250 ms window
 *   are deduplicated to avoid log spam from rapid repeated interactions.
 *
 * @param logger  Optional logger with an `info` method. If omitted or
 *                without `info`, events are observed but not logged
 *                (noop).
 * @returns       Cleanup function — decrements the document's install count.
 *                Listeners are removed only when the last cleanup is called.
 *                Safe to call more than once (idempotent).
 */

const ACTION_EVENT = 'app_action';
const MAX_TARGET_LENGTH = 80;
const COALESCE_WINDOW_MS = 250;

// eslint-disable-next-line no-unused-vars
type LoggerLike = { info?: (...args: any[]) => void };

// Base no-unused-vars flags type-context parameter names (same root cause as
// LoggerLike above); @typescript-eslint/no-unused-vars would handle this but
// is not configured in the logger's eslintrc.
/* eslint-disable no-unused-vars */
interface SharedState {
  refCount: number;
  onClick: (e: Event) => void;
  onSubmit: (e: Event) => void;
  onChange: (e: Event) => void;
}
/* eslint-enable no-unused-vars */

/**
 * Module-level shared state for document-level idempotence.
 * When non-null, the observer is active on the current document.
 */
let sharedState: SharedState | null = null;

/**
 * Truncate a string to `max` characters. Used for both target metadata and
 * route so neither field can grow unbounded.
 */
export function truncateAppActionValue(value: string, max = MAX_TARGET_LENGTH): string {
  return value.length > max ? value.slice(0, max) : value;
}

/**
 * Extract a human-readable target label from an element using attribute
 * priority: `data-testid` → `role` → tag name.
 *
 * User-derived attributes (`aria-label`, `name`) are intentionally excluded
 * to prevent capturing potentially sensitive user input (e.g. email text
 * used as a label or name) in breadcrumb metadata. Only developer-authored
 * static identifiers are used. The result is truncated to
 * {@link MAX_TARGET_LENGTH} characters.
 */
function extractTarget(element: Element): string {
  const candidates = [
    element.getAttribute('data-testid'),
    element.getAttribute('role'),
    element.tagName?.toLowerCase()
  ];

  for (const candidate of candidates) {
    if (candidate) {
      return truncateAppActionValue(candidate);
    }
  }
  return '';
}

/**
 * Returns `true` only for elements whose `change` event is meaningful as a
 * breadcrumb: `<select>`, `<input type="checkbox">`, and `<input type="radio">`.
 * Text inputs, email fields, etc. are excluded — their change events fire on
 * every blur and would flood the log.
 */
function isTrackableChangeTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName.toLowerCase();
  if (tag === 'select') {
    return true;
  }
  if (tag === 'input') {
    const type = (target.getAttribute('type') || 'text').toLowerCase();
    return type === 'checkbox' || type === 'radio';
  }
  return false;
}

/**
 * Create a reference-counted cleanup function for the given shared state.
 * Each cleanup is idempotent (calling it twice is a no-op). Listeners are
 * removed only when the refcount reaches zero **and** `sharedState` still
 * points at this state instance — this prevents a stale cleanup from a
 * previous install cycle from tearing down a fresh one.
 */
function createCleanup(state: SharedState): () => void {
  let cleaned = false;
  return () => {
    if (cleaned) {
      return;
    }
    cleaned = true;
    state.refCount--;
    if (state.refCount <= 0 && sharedState === state) {
      document.removeEventListener('click', state.onClick, true);
      document.removeEventListener('submit', state.onSubmit, true);
      document.removeEventListener('change', state.onChange, true);
      sharedState = null;
    }
  };
}

export function installAppActionBreadcrumbs(logger?: LoggerLike): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  // Idempotent: if already installed on this document, bump the refcount
  // and return a cleanup that decrements it. No new listeners are attached.
  // The first install's logger is retained for all events.
  if (sharedState) {
    sharedState.refCount++;
    return createCleanup(sharedState);
  }

  let lastEvent: { signature: string; timestamp: number } | null = null;

  const emit = (action: string, element: Element | null) => {
    if (!element) {
      return;
    }
    const target = extractTarget(element);
    if (!target) {
      return;
    }

    const signature = `${action}:${target}`;
    const now = Date.now();
    if (lastEvent && lastEvent.signature === signature && now - lastEvent.timestamp < COALESCE_WINDOW_MS) {
      return;
    }
    lastEvent = { signature, timestamp: now };

    try {
      logger?.info?.(ACTION_EVENT, {
        action,
        target,
        route: truncateAppActionValue(window.location?.pathname ?? '')
      });
    } catch {
      // Breadcrumb logging must never change app behavior.
    }
  };

  const onClick = (e: Event) => emit('click', e.target as Element | null);
  const onSubmit = (e: Event) => emit('submit', e.target as Element | null);
  const onChange = (e: Event) => {
    if (!isTrackableChangeTarget(e.target)) {
      return;
    }
    emit('change', e.target as Element | null);
  };

  // Capture phase so we observe the event before any app handler can stop
  // propagation. We never call stopPropagation/preventDefault, so the event
  // continues through the normal capture → target → bubble flow untouched.
  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('change', onChange, true);

  sharedState = { refCount: 1, onClick, onSubmit, onChange };

  return createCleanup(sharedState);
}
